import type { CustomerId, FacilityId } from "../lib/workIds";
import type { WorkRequestIntakeState } from "../types/workRequest";
import { workRequestIntakeStateSchema } from "../types/workRequestApi";
import { workClient } from "./workClient";

// Slightly above the backend's model timeout (INTAKE_INTERPRETATION_TIMEOUT_MS
// in the Work BFF) so a slow model normally comes back as a heuristic draft
// flagged UNAVAILABLE; this only fires when the server itself never answers.
export const INTAKE_REQUEST_TIMEOUT_MS = 20_000;

/** The intake request could not reach a working AI path: the network failed,
 * the request timed out, or the server answered 5xx. Callers send the
 * requester to the standard form instead of showing a generic failure. */
export class IntakeAiUnavailableError extends Error {
  constructor(message: string, options?: ErrorOptions) {
    super(message, options);
    this.name = "IntakeAiUnavailableError";
  }
}

export function isIntakeAiUnavailableError(
  error: unknown,
): error is IntakeAiUnavailableError {
  return error instanceof IntakeAiUnavailableError;
}

export async function sendWorkRequestIntakeMessage({
  customerId,
  facilityId,
  state,
  userMessage,
}: {
  customerId: CustomerId;
  facilityId: FacilityId;
  state: WorkRequestIntakeState;
  userMessage: string;
}) {
  const controller = new AbortController();
  const timeoutHandle = setTimeout(
    () => controller.abort(),
    INTAKE_REQUEST_TIMEOUT_MS,
  );

  const response = await workClient.v1.customers[":customerId"].facilities[
    ":facilityId"
  ].requests.intake.message
    .$post(
      {
        param: { customerId, facilityId },
        json: {
          stage: state.stage,
          traceId: state.traceId,
          userMessage,
          draft: state.draft,
          assetCandidates: state.assetCandidates,
        },
      },
      { init: { signal: controller.signal } },
    )
    .catch((error: unknown) => {
      // fetch rejects for a network failure and for our own abort.
      throw new IntakeAiUnavailableError(
        controller.signal.aborted
          ? "The Work Request intake timed out"
          : "The Work Request intake could not be reached",
        { cause: error },
      );
    })
    .finally(() => clearTimeout(timeoutHandle));

  if (response.status >= 500) {
    throw new IntakeAiUnavailableError(
      `Failed to process the Work Request: ${response.status}`,
    );
  }
  if (!response.ok) {
    throw new Error(`Failed to process the Work Request: ${response.status}`);
  }

  return workRequestIntakeStateSchema.parse(await response.json());
}
